import { cn } from '@/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { VariantProps, cva } from 'class-variance-authority';
import { PanelLeft } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import {
    useFluidHoverGroupItem,
    useInFluidHoverGroup
} from '@/components/ui/fluid-hover-group';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from '@/components/ui/tooltip';

import { useIsMobile } from '@/hooks/use-mobile';

const { useEffect } = React;

const SIDEBAR_COOKIE_NAME = 'sidebar:state';
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH_COOKIE_NAME = 'sidebar:width';
const SIDEBAR_DEFAULT_WIDTH = 280;
const SIDEBAR_WIDTH_MOBILE = '18rem';
const SIDEBAR_WIDTH_ICON = '3rem';
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';

type SidebarContext = {
    state: 'expanded' | 'collapsed';
    open: boolean;
    setOpen: (open: boolean) => void;
    openMobile: boolean;
    setOpenMobile: (open: boolean) => void;
    isMobile: boolean;
    toggleSidebar: () => void;
    sidebarWidth: number | undefined;
    setSidebarWidth: (width: number) => void;
    isResizing: boolean;
};

const SidebarContext = React.createContext<SidebarContext | null>(null);

function useSidebar() {
    const context = React.useContext(SidebarContext);
    if (!context) {
        throw new Error('useSidebar must be used within a SidebarProvider.');
    }

    return context;
}

const SidebarProvider = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<'div'> & {
        defaultOpen?: boolean;
        open?: boolean;
        onOpenChange?: (open: boolean) => void;
    }
>(
    (
        {
            defaultOpen = true,
            open: openProp,
            onOpenChange: setOpenProp,
            className,
            style,
            children,
            ...props
        },
        ref
    ) => {
        const isMobile = useIsMobile();
        const [openMobile, setOpenMobile] = React.useState(false);
        const [isResizing, setIsResizing] = React.useState(false);

        // Get initial width from localStorage or use default
        const getInitialWidth = () => {
            if (typeof window !== 'undefined') {
                const savedWidth = localStorage.getItem(
                    SIDEBAR_WIDTH_COOKIE_NAME
                );
                if (savedWidth !== null) {
                    const width = parseInt(savedWidth, 10);
                    // If defaultOpen is true, never start collapsed
                    if (width === 0 && defaultOpen) {
                        return SIDEBAR_DEFAULT_WIDTH;
                    }
                    return width;
                }
                // Check old cookie format for migration
                const savedState = document.cookie
                    .split('; ')
                    .find((row) => row.startsWith(SIDEBAR_COOKIE_NAME))
                    ?.split('=')[1];

                return savedState === 'false' && !defaultOpen
                    ? 0
                    : SIDEBAR_DEFAULT_WIDTH;
            }
            return defaultOpen ? SIDEBAR_DEFAULT_WIDTH : 0;
        };

        // Primary state: sidebar width in pixels (0 = collapsed)
        const [sidebarWidth, setSidebarWidthInternal] =
            React.useState(getInitialWidth);

        // Derived state: open/closed based on width
        const open = openProp ?? sidebarWidth != 0;

        // Width setter that also handles localStorage and callbacks
        const setSidebarWidth = React.useCallback(
            (width: number | undefined) => {
                setSidebarWidthInternal(width);

                // Save to localStorage
                localStorage.setItem(
                    SIDEBAR_WIDTH_COOKIE_NAME,
                    width?.toString() || ''
                );

                // Handle external open/close callbacks
                const isOpen = width != 0;
                if (setOpenProp) {
                    setOpenProp(isOpen);
                }

                // Keep legacy cookie for compatibility
                document.cookie = `${SIDEBAR_COOKIE_NAME}=${isOpen}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
            },
            [setOpenProp]
        );

        // Legacy setOpen function for compatibility
        const setOpen = React.useCallback(
            (value: boolean | ((value: boolean) => boolean)) => {
                const openState =
                    typeof value === 'function' ? value(open) : value;

                if (openState) {
                    // Opening: restore previous width or use default
                    const savedWidth = localStorage.getItem(
                        SIDEBAR_WIDTH_COOKIE_NAME
                    );

                    if (savedWidth === '0') {
                        setSidebarWidth(SIDEBAR_DEFAULT_WIDTH);
                        return;
                    }

                    const width = savedWidth
                        ? parseInt(savedWidth, 10)
                        : SIDEBAR_DEFAULT_WIDTH;
                    setSidebarWidth(
                        typeof width == 'number' ? width : SIDEBAR_DEFAULT_WIDTH
                    );
                } else {
                    // Closing: set width to 0
                    setSidebarWidth(0);
                }
            },
            [open, setSidebarWidth]
        );

        // Helper to toggle the sidebar.
        const toggleSidebar = React.useCallback(() => {
            return isMobile
                ? setOpenMobile((open) => !open)
                : setOpen((open) => !open);
        }, [isMobile, setOpen, setOpenMobile]);

        // Adds a keyboard shortcut to toggle the sidebar.
        React.useEffect(() => {
            const handleKeyDown = (event: KeyboardEvent) => {
                if (
                    event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
                    (event.metaKey || event.ctrlKey)
                ) {
                    event.preventDefault();
                    toggleSidebar();
                }
            };

            window.addEventListener('keydown', handleKeyDown);
            return () => window.removeEventListener('keydown', handleKeyDown);
        }, [toggleSidebar]);

        // We add a state so that we can do data-state="expanded" or "collapsed".
        // This makes it easier to style the sidebar with Tailwind classes.
        const state = open ? 'expanded' : 'collapsed';

        const contextValue = React.useMemo<SidebarContext>(
            () => ({
                state,
                open,
                setOpen,
                isMobile,
                openMobile,
                setOpenMobile,
                toggleSidebar,
                sidebarWidth,
                setSidebarWidth,
                isResizing,
                setIsResizing
            }),
            [
                state,
                open,
                setOpen,
                isMobile,
                openMobile,
                setOpenMobile,
                toggleSidebar,
                sidebarWidth,
                setSidebarWidth,
                isResizing
            ]
        );

        return (
            <SidebarContext.Provider value={contextValue}>
                <TooltipProvider>
                    <div
                        style={
                            {
                                '--sidebar-width': open
                                    ? `${sidebarWidth}px`
                                    : '0px',
                                '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
                                ...style
                            } as React.CSSProperties
                        }
                        className={cn(
                            'group/sidebar-wrapper flex has-data-[variant=inset]:bg-sidebar w-full min-h-svh',
                            isResizing && 'sidebar-resizing',
                            className
                        )}
                        ref={ref}
                        {...props}
                    >
                        {children}
                    </div>
                </TooltipProvider>
            </SidebarContext.Provider>
        );
    }
);
SidebarProvider.displayName = 'SidebarProvider';

const Sidebar = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<'div'> & {
        side?: 'left' | 'right';
        variant?: 'sidebar' | 'floating' | 'inset';
        collapsible?: 'offcanvas' | 'icon' | 'none';
        resizable?: boolean;
    }
>(
    (
        {
            side = 'left',
            variant = 'sidebar',
            collapsible = 'offcanvas',
            resizable = false,
            className,
            children,
            ...props
        },
        ref
    ) => {
        const {
            isMobile,
            state,
            openMobile,
            setOpenMobile,
            sidebarWidth,
            setSidebarWidth,
            isResizing,
            setIsResizing
        } = useSidebar();

        // Resize functionality - use the context's sidebarWidth and setSidebarWidth

        const MIN_WIDTH = 0;
        const MIN_WIDTH_THRESHOLD = 220; // Below this width, collapse to icon mode
        const MAX_WIDTH = window.innerWidth * 0.5; // 80% of viewport width

        let prevWidth: number | undefined;
        const startResizing = React.useCallback(
            (mouseDownEvent: React.MouseEvent) => {
                if (!resizable) {
                    return;
                }

                mouseDownEvent.preventDefault();
                setIsResizing(true);

                // Add global class to disable all sidebar transitions
                document.body.classList.add('sidebar-resizing');

                const startX = mouseDownEvent.clientX;
                const element = document.getElementById('resizable-sidebar');
                const startWidth = element
                    ? element.getBoundingClientRect().width
                    : 120;

                // Prevent text selection while dragging
                document.body.style.userSelect = 'none';
                document.body.style.cursor = 'col-resize';

                let rafId: number | null = null;

                let isDownSizing = false;
                const doDrag = (mouseMoveEvent: MouseEvent) => {
                    mouseMoveEvent.preventDefault();

                    // Cancel previous frame if it hasn't been processed yet
                    if (rafId) {
                        cancelAnimationFrame(rafId);
                    }

                    rafId = requestAnimationFrame(() => {
                        const newWidth =
                            startWidth + (mouseMoveEvent.clientX - startX);
                        const constrainedWidth = Math.min(
                            Math.max(newWidth, MIN_WIDTH),
                            MAX_WIDTH
                        );
                        const isLeftDragging = newWidth < startWidth;
                        isDownSizing = newWidth < (prevWidth || 0);
                        prevWidth = newWidth;
                        if (
                            (isLeftDragging || isDownSizing) &&
                            constrainedWidth < MIN_WIDTH_THRESHOLD
                        ) {
                            // Collapse to icon mode
                            setSidebarWidth(0); // Icon width
                            return;
                        }
                        setSidebarWidth(constrainedWidth);
                    });
                };

                const stopDrag = (mouseMoveEvent: MouseEvent) => {
                    const newWidth =
                        startWidth + (mouseMoveEvent.clientX - startX);

                    if (
                        !isDownSizing &&
                        newWidth > startWidth &&
                        newWidth < MIN_WIDTH_THRESHOLD
                    ) {
                        // If released between icon size and threshold, snap to threshold
                        setSidebarWidth(MIN_WIDTH_THRESHOLD);
                    }

                    if (rafId) {
                        cancelAnimationFrame(rafId);
                    }

                    setIsResizing(false);

                    // Restore text selection and cursor
                    document.body.style.userSelect = '';
                    document.body.style.cursor = '';

                    // Remove the global no-transition class
                    document.body.classList.remove('sidebar-resizing');

                    // No need to save to localStorage here - setSidebarWidth handles it
                    document.removeEventListener('mousemove', doDrag);
                    document.removeEventListener('mouseup', stopDrag);
                };

                document.addEventListener('mousemove', doDrag, {
                    passive: false
                });
                document.addEventListener('mouseup', stopDrag);
            },
            [sidebarWidth, resizable, MIN_WIDTH, MAX_WIDTH]
        );

        // Keyboard shortcuts for resizing
        React.useEffect(() => {
            if (!resizable) return;

            const handleKeyDown = (e: KeyboardEvent) => {
                if (e.ctrlKey || e.metaKey) {
                    if (e.key === '[') {
                        e.preventDefault();
                        const element =
                            document.getElementById('resizable-sidebar');
                        const currentWidth = element
                            ? element.getBoundingClientRect().width
                            : sidebarWidth || 0;
                        const newWidth = Math.max(currentWidth - 20, MIN_WIDTH);
                        setSidebarWidth(newWidth);
                    } else if (e.key === ']') {
                        e.preventDefault();
                        const element =
                            document.getElementById('resizable-sidebar');
                        const currentWidth = element
                            ? element.getBoundingClientRect().width
                            : sidebarWidth || 0;
                        const newWidth = Math.min(currentWidth + 20, MAX_WIDTH);
                        setSidebarWidth(newWidth);
                    }
                }
            };

            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }, [MIN_WIDTH, MAX_WIDTH, resizable, sidebarWidth, setSidebarWidth]);

        // Prevent sidebar interactions from stealing focus when input is focused
        useEffect(() => {
            const handleMouseDown = (e: MouseEvent) => {
                // Check if an input element is currently focused
                const activeElement = document.activeElement;
                const isInputFocused =
                    activeElement &&
                    (activeElement.tagName === 'INPUT' ||
                        activeElement.tagName === 'TEXTAREA' ||
                        (activeElement as HTMLElement).contentEditable ===
                            'true' ||
                        activeElement.getAttribute('role') === 'textbox');

                if (!isInputFocused) return;

                // Check if the click is inside a sidebar
                const clickTarget = e.target as HTMLElement;
                const isInsideSidebar =
                    clickTarget.closest('[data-sidebar="sidebar"]') !== null;

                // Check if this is the sidebar trigger button (should always work normally)
                const isSidebarTrigger =
                    clickTarget.closest('#sidebar-trigger-button') !== null ||
                    clickTarget.closest('[data-sidebar="trigger"]') !== null;

                // Allow sidebar trigger to work normally - don't prevent its behavior
                if (isSidebarTrigger) {
                    // Store the focused element to restore after sidebar action
                    const focusedElement = activeElement as HTMLElement;

                    // Allow the sidebar trigger to execute, then restore focus
                    setTimeout(() => {
                        focusedElement.focus();
                    }, 100); // Slightly longer delay to ensure sidebar animation completes

                    return;
                }

                if (isInsideSidebar) {
                    // Store the currently focused element
                    const focusedElement = activeElement as HTMLElement;

                    // Don't prevent the event - let it work normally
                    // But restore focus after the interaction completes
                    setTimeout(() => {
                        focusedElement.focus();
                    }, 50); // Shorter delay since we're not preventing anything
                }
            };

            // Use global event listener with capture to catch all mousedown events
            document.addEventListener('mousedown', handleMouseDown, {
                capture: true
            });

            return () => {
                document.removeEventListener('mousedown', handleMouseDown, {
                    capture: true
                });
            };
        }, []);

        if (collapsible === 'none') {
            return (
                <div
                    className={cn(
                        'flex flex-col bg-sidebar w-(--sidebar-width) h-full text-sidebar-foreground',
                        className
                    )}
                    ref={ref}
                    {...props}
                >
                    {children}
                </div>
            );
        }

        if (isMobile) {
            return (
                <Sheet
                    open={openMobile}
                    onOpenChange={setOpenMobile}
                    {...props}
                >
                    <SheetContent
                        data-sidebar="sidebar"
                        data-mobile="true"
                        className="[&>button]:hidden bg-sidebar p-0 w-(--sidebar-width) text-sidebar-foreground"
                        style={
                            {
                                '--sidebar-width': SIDEBAR_WIDTH_MOBILE
                            } as React.CSSProperties
                        }
                        side={side}
                    >
                        <div className="flex flex-col w-full h-full">
                            {children}
                        </div>
                    </SheetContent>
                </Sheet>
            );
        }

        return (
            <div
                ref={ref}
                className={cn(
                    'group peer text-sidebar-foreground',
                    'hidden md:block'
                )}
                data-state={state}
                data-collapsible={state === 'collapsed' ? collapsible : ''}
                data-variant={variant}
                data-side={side}
            >
                <div
                    id="resizable-sidebar"
                    className={cn(
                        'hidden left-0 z-10 sticky inset-y-0 md:flex h-svh duration-200 ease-linear',
                        side === 'left'
                            ? 'left-0 group-data-[collapsible=offcanvas]:w-0'
                            : 'right-0 group-data-[collapsible=offcanvas]:-right-(--sidebar-width)',
                        // Adjust the padding for floating and inset variants.
                        variant === 'floating' || variant === 'inset'
                            ? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]'
                            : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l',
                        // Only add transition when not actively resizing
                        !isResizing && 'transition-[width]',
                        // Add isolation to prevent affecting children
                        'isolate',
                        className
                    )}
                    style={{
                        width: resizable
                            ? typeof sidebarWidth === 'number'
                                ? `${Math.max(sidebarWidth, 56)}px`
                                : 'auto'
                            : undefined,
                        // Add will-change during resize for better performance
                        willChange: isResizing ? 'width' : 'auto'
                    }}
                    {...props}
                >
                    <div
                        data-sidebar="sidebar"
                        className={cn(
                            'relative flex flex-col bg-sidebar group-data-[variant=floating]:shadow-sm group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:rounded-lg w-full h-full',
                            // Disable all transitions during resize
                            isResizing && '**:transition-none!'
                        )}
                    >
                        {children}

                        {/* Resize handle - positioned within the fixed sidebar */}
                        {resizable && side === 'left' && (
                            <div
                                className={cn(
                                    'group/resize top-0 right-auto left-full z-50 absolute w-4 h-full transition-all duration-200 cursor-col-resize',
                                    'bg-transparent hover:bg-linear-to-b hover:from-blue-500/20 hover:to-purple-500/20',
                                    'border-r border-transparent hover:border-blue-500/30',
                                    isResizing &&
                                        'bg-linear-to-b from-blue-500/30 to-purple-500/30 border-blue-500/50'
                                )}
                                onMouseDown={startResizing}
                                title="Drag to resize sidebar"
                            >
                                {/* Subtle drag indicator dots */}
                                <div
                                    className={cn(
                                        'top-1/2 left-1/2 absolute flex flex-col gap-1 transition-opacity -translate-x-1/2 -translate-y-1/2 duration-200 transform',
                                        'opacity-0 group-hover/resize:opacity-60',
                                        isResizing && 'opacity-80'
                                    )}
                                >
                                    <div className="bg-blue-500 rounded-full w-0.5 h-0.5"></div>
                                    <div className="bg-blue-500 rounded-full w-0.5 h-0.5"></div>
                                    <div className="bg-blue-500 rounded-full w-0.5 h-0.5"></div>
                                    <div className="bg-blue-500 rounded-full w-0.5 h-0.5"></div>
                                    <div className="bg-blue-500 rounded-full w-0.5 h-0.5"></div>
                                </div>

                                {/* Hover expansion area for easier grabbing */}
                                <div className="left-0 absolute inset-y-0 group-hover/resize:bg-transparent"></div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
);
Sidebar.displayName = 'Sidebar';

const SidebarTrigger = React.forwardRef<
    React.ElementRef<typeof Button>,
    React.ComponentProps<typeof Button>
>(({ className, onClick, ...props }, ref) => {
    const { toggleSidebar } = useSidebar();

    return (
        <Button
            ref={ref}
            data-sidebar="trigger"
            variant="ghost"
            size="icon"
            className={cn('w-7 h-7', className)}
            onClick={(event) => {
                onClick?.(event);
                toggleSidebar();
            }}
            {...props}
        >
            <PanelLeft />
            <span className="sr-only">Toggle Sidebar</span>
        </Button>
    );
});
SidebarTrigger.displayName = 'SidebarTrigger';

const SidebarRail = React.forwardRef<
    HTMLButtonElement,
    React.ComponentProps<'button'>
>(({ className, ...props }, ref) => {
    const { toggleSidebar } = useSidebar();

    return (
        <button
            ref={ref}
            data-sidebar="rail"
            aria-label="Toggle Sidebar"
            tabIndex={-1}
            onClick={toggleSidebar}
            title="Toggle Sidebar"
            className={cn(
                'hidden group-data-[side=left]:-right-4 after:left-1/2 group-data-[side=right]:left-0 z-20 absolute after:absolute inset-y-0 after:inset-y-0 sm:flex hover:after:bg-sidebar-border w-4 after:w-[2px] transition-all -translate-x-1/2 ease-linear',
                'in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize',
                '[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize',
                'group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full hover:group-data-[collapsible=offcanvas]:bg-sidebar',
                '[[data-side=left][data-collapsible=offcanvas]_&]:-right-2',
                '[[data-side=right][data-collapsible=offcanvas]_&]:-left-2',
                className
            )}
            {...props}
        />
    );
});
SidebarRail.displayName = 'SidebarRail';

const SidebarInset = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<'main'>
>(({ className, ...props }, ref) => {
    return (
        <main
            ref={ref}
            className={cn(
                'relative flex flex-col flex-1 bg-background w-full min-w-0 min-h-svh',
                'peer-data-[variant=inset]:min-h-[calc(100svh-(--spacing(4)))] md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm',
                // Isolate content from sidebar transitions
                'isolate',
                className
            )}
            {...props}
        />
    );
});
SidebarInset.displayName = 'SidebarInset';

const SidebarInput = React.forwardRef<
    React.ElementRef<typeof Input>,
    React.ComponentProps<typeof Input>
>(({ className, ...props }, ref) => {
    return (
        <Input
            ref={ref}
            data-sidebar="input"
            className={cn(
                'bg-background shadow-none focus-visible:ring-2 focus-visible:ring-sidebar-ring w-full h-8',
                className
            )}
            {...props}
        />
    );
});
SidebarInput.displayName = 'SidebarInput';

const SidebarHeader = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
    return (
        <div
            ref={ref}
            data-sidebar="header"
            className={cn('flex flex-col gap-2 p-2', className)}
            {...props}
        />
    );
});
SidebarHeader.displayName = 'SidebarHeader';

const SidebarFooter = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
    return (
        <div
            ref={ref}
            data-sidebar="footer"
            className={cn('flex flex-col gap-2 p-2', className)}
            {...props}
        />
    );
});
SidebarFooter.displayName = 'SidebarFooter';

const SidebarSeparator = React.forwardRef<
    React.ElementRef<typeof Separator>,
    React.ComponentProps<typeof Separator>
>(({ className, ...props }, ref) => {
    return (
        <Separator
            ref={ref}
            data-sidebar="separator"
            className={cn('mx-2 bg-sidebar-border w-auto', className)}
            {...props}
        />
    );
});
SidebarSeparator.displayName = 'SidebarSeparator';

const SidebarContent = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
    return (
        <div
            ref={ref}
            data-sidebar="content"
            className={cn(
                'flex flex-col flex-1 gap-2 min-h-0 overflow-auto group-data-[collapsible=icon]:overflow-hidden',
                className
            )}
            {...props}
        />
    );
});
SidebarContent.displayName = 'SidebarContent';

const SidebarGroup = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<'div'>
>(({ className, ...props }, ref) => {
    return (
        <div
            ref={ref}
            data-sidebar="group"
            className={cn(
                'relative flex flex-col p-2 w-full min-w-0',
                className
            )}
            {...props}
        />
    );
});
SidebarGroup.displayName = 'SidebarGroup';

const SidebarGroupLabel = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<'div'> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'div';

    return (
        <Comp
            ref={ref as any}
            data-sidebar="group-label"
            className={cn(
                'flex items-center px-2 rounded-md outline-hidden ring-sidebar-ring focus-visible:ring-2 h-8 [&>svg]:size-4 font-medium text-sidebar-foreground/70 text-sm transition-[margin,opa] duration-200 ease-linear shrink-0 [&>svg]:shrink-0',
                'group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0',
                className
            )}
            {...props}
        />
    );
});
SidebarGroupLabel.displayName = 'SidebarGroupLabel';

const SidebarGroupAction = React.forwardRef<
    HTMLButtonElement,
    React.ComponentProps<'button'> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return (
        <Comp
            ref={ref as any}
            data-sidebar="group-action"
            className={cn(
                'top-3.5 right-3 absolute flex justify-center items-center hover:bg-sidebar-accent p-0 rounded-md outline-hidden ring-sidebar-ring focus-visible:ring-2 w-5 [&>svg]:size-4 aspect-square text-sidebar-foreground transition-transform hover:text-sidebar-accent-foreground [&>svg]:shrink-0',
                // Increases the hit area of the button on mobile.
                'after:absolute after:-inset-2 md:after:hidden',
                'group-data-[collapsible=icon]:hidden',
                className
            )}
            {...props}
        />
    );
});
SidebarGroupAction.displayName = 'SidebarGroupAction';

const SidebarGroupContent = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
    <div
        ref={ref}
        data-sidebar="group-content"
        className={cn('w-full text-sm', className)}
        {...props}
    />
));
SidebarGroupContent.displayName = 'SidebarGroupContent';

const SidebarMenu = React.forwardRef<
    HTMLUListElement,
    React.ComponentProps<'ul'>
>(({ className, ...props }, ref) => (
    <ul
        ref={ref}
        data-sidebar="menu"
        className={cn('flex flex-col gap-1 w-full min-w-0', className)}
        {...props}
    />
));
SidebarMenu.displayName = 'SidebarMenu';

const SidebarMenuItem = React.forwardRef<
    HTMLLIElement,
    React.ComponentProps<'li'>
>(({ className, ...props }, ref) => (
    <li
        ref={ref}
        data-sidebar="menu-item"
        className={cn('group/menu-item relative', className)}
        {...props}
    />
));
SidebarMenuItem.displayName = 'SidebarMenuItem';

const sidebarMenuButtonVariants = cva(
    'peer/menu-button [--icon-stroke:1.5] flex items-center gap-0 data-[active=true]:bg-sidebar-accent data-[state=open]:hover:bg-sidebar-accent hover:bg-sidebar-accent active:bg-sidebar-accent aria-disabled:opacity-50 disabled:opacity-50 p-2 group-data-[collapsible=icon]:p-2! group-has-data-[sidebar=menu-action]/menu-item:pr-8 rounded-md outline-hidden ring-sidebar-ring focus-visible:ring-2 w-full [&>svg]:size-4 group-data-[collapsible=icon]:size-8! overflow-hidden data-[active=true]:font-medium text-base text-left [&>span:last-child]:truncate transition-[width,height,padding] data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:text-sidebar-accent-foreground hover:text-sidebar-accent-foreground active:text-sidebar-accent-foreground aria-disabled:pointer-events-none disabled:pointer-events-none [&>svg]:shrink-0',
    {
        variants: {
            variant: {
                default:
                    'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                outline:
                    'bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]'
            },
            size: {
                default: 'h-8 text-base',
                sm: 'h-7 text-sm',
                lg: 'h-12 text-base group-data-[collapsible=icon]:p-0!'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
);

const SidebarMenuButton = React.forwardRef<
    HTMLButtonElement,
    React.ComponentProps<'button'> & {
        asChild?: boolean;
        isActive?: boolean;
        tooltip?: string | React.ComponentProps<typeof TooltipContent>;
    } & VariantProps<typeof sidebarMenuButtonVariants>
>(
    (
        {
            asChild = false,
            isActive = false,
            variant = 'default',
            size = 'default',
            tooltip,
            className,
            ...props
        },
        ref
    ) => {
        const Comp = asChild ? Slot : 'button';
        const { isMobile, state } = useSidebar();
        const inFluidGroup = useInFluidHoverGroup();
        const itemRef = useFluidHoverGroupItem<HTMLButtonElement>(
            ref as React.Ref<HTMLButtonElement>
        );

        const button = (
            <Comp
                ref={itemRef as any}
                data-sidebar="menu-button"
                data-size={size}
                data-active={isActive}
                className={cn(
                    sidebarMenuButtonVariants({ variant, size }),
                    inFluidGroup &&
                        'hover:bg-transparent active:bg-transparent data-[state=open]:hover:bg-transparent',
                    className
                )}
                {...props}
            />
        );

        if (!tooltip) {
            return button;
        }

        if (typeof tooltip === 'string') {
            tooltip = {
                children: tooltip
            };
        }

        return (
            <Tooltip>
                <TooltipTrigger asChild>{button}</TooltipTrigger>
                <TooltipContent
                    side="right"
                    align="center"
                    hidden={state !== 'collapsed' || isMobile}
                    {...tooltip}
                />
            </Tooltip>
        );
    }
);
SidebarMenuButton.displayName = 'SidebarMenuButton';

const SidebarMenuAction = React.forwardRef<
    HTMLButtonElement,
    React.ComponentProps<'button'> & {
        asChild?: boolean;
        showOnHover?: boolean;
    }
>(({ className, asChild = false, showOnHover = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return (
        <Comp
            ref={ref as any}
            data-sidebar="menu-action"
            className={cn(
                'top-1.5 right-1 absolute flex justify-center items-center hover:bg-sidebar-accent p-0 rounded-md outline-hidden ring-sidebar-ring focus-visible:ring-2 w-5 [&>svg]:size-4 aspect-square text-sidebar-foreground transition-transform hover:text-sidebar-accent-foreground peer-hover/menu-button:text-sidebar-accent-foreground [&>svg]:shrink-0',
                // Increases the hit area of the button on mobile.
                'after:absolute after:-inset-2 md:after:hidden',
                'peer-data-[size=sm]/menu-button:top-1',
                'peer-data-[size=default]/menu-button:top-1.5',
                'peer-data-[size=lg]/menu-button:top-2.5',
                'group-data-[collapsible=icon]:hidden',
                showOnHover &&
                    'group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 peer-data-[active=true]/menu-button:text-sidebar-accent-foreground md:opacity-0',
                className
            )}
            {...props}
        />
    );
});
SidebarMenuAction.displayName = 'SidebarMenuAction';

const SidebarMenuBadge = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
    <div
        ref={ref}
        data-sidebar="menu-badge"
        className={cn(
            'right-1 absolute flex justify-center items-center px-1 rounded-md min-w-5 h-5 font-medium tabular-nums text-sidebar-foreground text-sm pointer-events-none select-none',
            'peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground',
            'peer-data-[size=sm]/menu-button:top-1',
            'peer-data-[size=default]/menu-button:top-1.5',
            'peer-data-[size=lg]/menu-button:top-2.5',
            'group-data-[collapsible=icon]:hidden',
            className
        )}
        {...props}
    />
));
SidebarMenuBadge.displayName = 'SidebarMenuBadge';

const SidebarMenuSkeleton = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<'div'> & {
        showIcon?: boolean;
    }
>(({ className, showIcon = false, ...props }, ref) => {
    // Random width between 50 to 90%.
    const width = React.useMemo(() => {
        return `${Math.floor(Math.random() * 40) + 50}%`;
    }, []);

    return (
        <div
            ref={ref}
            data-sidebar="menu-skeleton"
            className={cn(
                'flex items-center gap-2 px-2 rounded-md h-8',
                className
            )}
            {...props}
        >
            {showIcon && (
                <Skeleton
                    className="rounded-md size-4"
                    data-sidebar="menu-skeleton-icon"
                />
            )}
            <Skeleton
                className="flex-1 max-w-(--skeleton-width) h-4"
                data-sidebar="menu-skeleton-text"
                style={
                    {
                        '--skeleton-width': width
                    } as React.CSSProperties
                }
            />
        </div>
    );
});
SidebarMenuSkeleton.displayName = 'SidebarMenuSkeleton';

const SidebarMenuSub = React.forwardRef<
    HTMLUListElement,
    React.ComponentProps<'ul'>
>(({ className, ...props }, ref) => (
    <ul
        ref={ref}
        data-sidebar="menu-sub"
        className={cn(
            'flex flex-col gap-1 mx-3.5 px-2.5 py-0.5 border-sidebar-border border-l min-w-0 translate-x-px',
            'group-data-[collapsible=icon]:hidden',
            className
        )}
        {...props}
    />
));
SidebarMenuSub.displayName = 'SidebarMenuSub';

const SidebarMenuSubItem = React.forwardRef<
    HTMLLIElement,
    React.ComponentProps<'li'>
>(({ ...props }, ref) => <li ref={ref} {...props} />);
SidebarMenuSubItem.displayName = 'SidebarMenuSubItem';

const SidebarMenuSubButton = React.forwardRef<
    HTMLAnchorElement,
    React.ComponentProps<'a'> & {
        asChild?: boolean;
        size?: 'sm' | 'md';
        isActive?: boolean;
    }
>(({ asChild = false, size = 'md', isActive, className, ...props }, ref) => {
    const Comp = asChild ? Slot : 'a';

    return (
        <Comp
            ref={ref as any}
            data-sidebar="menu-sub-button"
            data-size={size}
            data-active={isActive}
            className={cn(
                'flex items-center gap-2 hover:bg-sidebar-accent active:bg-sidebar-accent aria-disabled:opacity-50 disabled:opacity-50 px-2 rounded-md outline-hidden ring-sidebar-ring focus-visible:ring-2 min-w-0 h-7 [&>svg]:size-4 overflow-hidden text-sidebar-foreground [&>span:last-child]:truncate -translate-x-px [&>svg]:text-sidebar-accent-foreground hover:text-sidebar-accent-foreground active:text-sidebar-accent-foreground aria-disabled:pointer-events-none disabled:pointer-events-none [&>svg]:shrink-0',
                'data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground',
                size === 'sm' && 'text-sm',
                size === 'md' && 'text-sm',
                'group-data-[collapsible=icon]:hidden',
                className
            )}
            {...props}
        />
    );
});
SidebarMenuSubButton.displayName = 'SidebarMenuSubButton';

export {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupAction,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarInput,
    SidebarInset,
    SidebarMenu,
    SidebarMenuAction,
    SidebarMenuBadge,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSkeleton,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    SidebarProvider,
    SidebarRail,
    SidebarSeparator,
    SidebarTrigger,
    useSidebar
};
